import { api } from './api';

export type AuthType = 'none' | 'basic' | 'api_key' | 'bearer';

export interface RetryConfig {
  enabled: boolean;
  max_attempts: number;
  backoff_multiplier: number;
}

export interface Webhook {
  id: string;
  clinic_id: string;
  name?: string;
  description?: string;
  webhook_url: string;
  auth_type: AuthType;
  subscribed_events: string[];
  enabled: boolean;
  retry_config: RetryConfig;
  timeout_ms: number;
  custom_headers: Record<string, string>;
  last_triggered_at?: string;
  last_failure_at?: string;
  failure_count: number;
  total_deliveries: number;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CreateWebhookRequest {
  name?: string;
  description?: string;
  webhook_url: string;
  auth_type?: AuthType;
  api_key?: string;
  bearer_token?: string;
  webhook_username?: string;
  webhook_password?: string;
  subscribed_events?: string[];
  enabled?: boolean;
  retry_config?: {
    enabled?: boolean;
    max_attempts?: number;
    backoff_multiplier?: number;
  };
  timeout_ms?: number;
  custom_headers?: Record<string, string>;
  metadata?: Record<string, any>;
}

export interface WebhookStats {
  total_deliveries: number;
  failure_count: number;
  last_triggered_at?: string;
  last_failure_at?: string;
  is_healthy: boolean;
  enabled: boolean;
  subscribed_events: string[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: {
    message: string;
  };
  count?: number;
}

export interface WebhookLog {
  id: string;
  webhook_id: string | null;
  clinic_id: string | null;
  event_type: string;
  event_id: string | null;
  request_url: string;
  request_payload: Record<string, unknown>;
  request_headers: Record<string, string> | null;
  response_status_code: number | null;
  response_time_ms: number | null;
  success: boolean;
  error_message: string | null;
  attempt_number: number;
  retry_of_log_id: string | null;
  created_at: string;
  updated_at: string;
  webhook?: {
    id: string;
    name: string | null;
    webhook_url: string;
  };
  retryLogs?: WebhookLog[];
  originalLog?: WebhookLog | null;
}

export interface WebhookLogsResponse {
  success: boolean;
  data: WebhookLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface WebhookLogDetailResponse {
  success: boolean;
  data: WebhookLog;
}

export interface WebhookLogFilters {
  page?: number;
  limit?: number;
  event_type?: string;
  success?: boolean | string;
  start_date?: string;
  end_date?: string;
}

class WebhookService {
  /**
   * List all webhooks for a clinic
   */
  async listWebhooks(clinicId: string): Promise<Webhook[]> {
    const response = await api.get<ApiResponse<Webhook[]>>(
      `/clinic/${clinicId}/webhooks`
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch webhooks');
    }
    return response.data || [];
  }

  /**
   * Get a single webhook by ID
   */
  async getWebhook(clinicId: string, webhookId: string): Promise<Webhook> {
    const response = await api.get<ApiResponse<Webhook>>(
      `/clinic/${clinicId}/webhooks/${webhookId}`
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch webhook');
    }
    return response.data;
  }

  /**
   * Create a new webhook
   */
  async createWebhook(
    clinicId: string,
    data: CreateWebhookRequest
  ): Promise<Webhook> {
    const response = await api.post<ApiResponse<Webhook>>(
      `/clinic/${clinicId}/webhooks`,
      data
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create webhook');
    }
    return response.data;
  }

  /**
   * Update an existing webhook
   */
  async updateWebhook(
    clinicId: string,
    webhookId: string,
    data: Partial<CreateWebhookRequest>
  ): Promise<Webhook> {
    const response = await api.put<ApiResponse<Webhook>>(
      `/clinic/${clinicId}/webhooks/${webhookId}`,
      data
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update webhook');
    }
    return response.data;
  }

  /**
   * Delete a webhook
   */
  async deleteWebhook(clinicId: string, webhookId: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(
      `/clinic/${clinicId}/webhooks/${webhookId}`
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete webhook');
    }
  }

  /**
   * Get webhook statistics
   */
  async getWebhookStats(
    clinicId: string,
    webhookId: string
  ): Promise<WebhookStats> {
    const response = await api.get<ApiResponse<WebhookStats>>(
      `/clinic/${clinicId}/webhooks/${webhookId}/stats`
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch webhook stats');
    }
    return response.data;
  }

  /**
   * Get webhook logs with pagination and filtering
   */
  async getWebhookLogs(
    clinicId: string,
    webhookId: string,
    filters?: WebhookLogFilters
  ): Promise<WebhookLogsResponse> {
    const params = new URLSearchParams();
    
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.event_type) params.append('event_type', filters.event_type);
    if (filters?.success !== undefined && filters.success !== 'all') {
      params.append('success', filters.success.toString());
    }
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const queryString = params.toString();
    const url = `/clinic/${clinicId}/webhooks/${webhookId}/logs${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<WebhookLogsResponse>(url);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch webhook logs');
    }
    return response;
  }

  /**
   * Get a specific webhook log with retry chain
   */
  async getWebhookLogDetail(
    clinicId: string,
    webhookId: string,
    logId: string
  ): Promise<WebhookLog> {
    const response = await api.get<WebhookLogDetailResponse>(
      `/clinic/${clinicId}/webhooks/${webhookId}/logs/${logId}`
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch webhook log');
    }
    return response.data;
  }
}

export const webhookService = new WebhookService();
export default webhookService;

